import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/client";
import { uploadManagedFile } from "@/utils/managedFile";
import type { ScanStatus } from "@/components/ui/ManagedFileLink";

export type ExtensionStatus =
  | "pending"
  | "approved"
  | "approved_conditional"
  | "rejected";

export interface ScopeExtensionAttachment {
  id: string;
  original_filename: string;
  size_bytes: number | null;
  scan_status: ScanStatus;
  url: string | null;
}

export interface ScopeExtensionRequest {
  id: string;
  contractor: string;
  contractor_name: string;
  categories: { id: string; name: string }[];
  positions: { id: string; name: string; category_name: string }[];
  voivodeships: string[];
  voivodeship_labels: string[];
  description: string;
  attachments: ScopeExtensionAttachment[];
  status: ExtensionStatus;
  status_label: string;
  decision_note: string;
  decided_by_name: string | null;
  decided_at: string | null;
  submitted_by_name: string | null;
  created_at: string;
}

export interface ScopeExtensionPayload {
  categories: string[];
  /** Pojedyncze roboty — tylko w kategorii posiadanej w zawężonym zakresie. */
  positions: string[];
  voivodeships: string[];
  description: string;
  attachments: string[];
}

export const EXTENSION_STATUS_COLOR: Record<
  ExtensionStatus,
  "gray" | "green" | "yellow" | "red" | "blue"
> = {
  pending: "blue",
  approved: "green",
  approved_conditional: "yellow",
  rejected: "red",
};

export function useScopeRequests(status?: ExtensionStatus) {
  return useQuery({
    queryKey: ["rfq", "scope-requests", status ?? "all"],
    queryFn: () =>
      apiClient
        .get<{ data: ScopeExtensionRequest[] }>("/rfq/scope-requests/", {
          params: status ? { status } : undefined,
        })
        .then((r) => r.data.data),
  });
}

export function useSubmitScopeRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ScopeExtensionPayload) =>
      apiClient.post("/rfq/scope-requests/", payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["rfq", "scope-requests"] }),
  });
}

export function useDecideScopeRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      decision,
      note,
    }: {
      id: string;
      decision: Exclude<ExtensionStatus, "pending">;
      note: string;
    }) => apiClient.post(`/rfq/scope-requests/${id}/decide/`, { decision, note }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["rfq", "scope-requests"] });
      qc.invalidateQueries({ queryKey: ["rfq", "qualification"] });
    },
  });
}

/**
 * Wgranie pliku do wspólnego rejestru — zwraca id do podpięcia pod wniosek.
 * Plik ponad limit rejestru odrzuca od razu (FileTooLargeError, utils/managedFile).
 */
export async function uploadAttachment(file: File): Promise<string> {
  return (await uploadManagedFile(file)).id;
}
